import { rgba } from 'polished';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import { useTheme } from 'styled-components';

import { Card, Delta, Heading3, Paragraph, Skeleton } from '@treams/treams-ui';

import { ratio } from '../cycles/CycleAnalytics';
import { DetailsList } from '../cycles/components/CycleDetails';
import { LegendSwatch } from '../cycles/components/CycleMomentDistributionChart';
import { CycleProgressChart } from '../cycles/components/CycleProgressChart';
import { ProgressLabel } from '../cycles/components/GridProgressCell';

export function TalentReviewActivityCard({
	placedCount,
	eligibleCount,
	previousPlacedCount,
	previousEligibleCount,
}: {
	placedCount: number;
	eligibleCount: number;
	previousPlacedCount: number;
	previousEligibleCount: number;
}) {
	const { t } = useTranslation();
	const theme = useTheme();
	const openCount = Math.max(eligibleCount - placedCount, 0);

	return (
		<Card height="100%">
			<Heading3 mb={2}>{t('analytics.talentReview.activity.title')}</Heading3>
			<Paragraph color="slateBlue">{t('analytics.talentReview.activity.subtitle')}</Paragraph>
			<Box mt={3}>
				<Flex alignItems="center" flexWrap="wrap" style={{ gap: '8px 16px' }}>
					<CycleProgressChart completed={placedCount} started={openCount} total={eligibleCount} />
					<Flex flexDirection="column" style={{ gap: 8 }}>
						<Paragraph color="slateBlue">
							{t('analytics.talentReview.activity.completedCount', {
								completed: placedCount,
								total: eligibleCount,
							})}
						</Paragraph>
						<Delta
							currentValue={ratio(placedCount, eligibleCount)}
							previousValue={ratio(previousPlacedCount, previousEligibleCount)}
							label={t('analytics.talentReview.activity.delta')}
						/>
					</Flex>
				</Flex>
				<Box mt={3}>
					<DetailsList
						items={[
							{
								icon: <LegendSwatch swatchColor={theme.colors.primary} />,
								title: t('analytics.talentReview.activity.completed'),
								description: <ProgressLabel value={placedCount} total={eligibleCount} />,
							},
							{
								icon: <LegendSwatch swatchColor={rgba(theme.colors.primary, 0.5)} />,
								title: t('analytics.talentReview.activity.open'),
								description: <ProgressLabel value={openCount} total={eligibleCount} />,
							},
							{
								icon: <LegendSwatch swatchColor={rgba(theme.colors.primary, 0.12)} />,
								title: t('analytics.talentReview.activity.planned'),
								description: <ProgressLabel value={0} total={eligibleCount} />,
							},
						]}
					/>
				</Box>
			</Box>
		</Card>
	);
}

TalentReviewActivityCard.Skeleton = function TalentReviewActivityCardSkeleton() {
	return (
		<Card height="100%">
			<Heading3.Skeleton width={120} />
			<Paragraph.Skeleton lines={1} mt={2} width={220} />
			<Box mt={3}>
				<Flex alignItems="center" flexWrap="wrap" style={{ gap: '8px 16px' }}>
					<Skeleton.Box width={80} height={80} style={{ borderRadius: '50%' }} />
					<Flex flexDirection="column" style={{ gap: 8 }}>
						<Paragraph.Skeleton lines={1} width={180} />
						<Paragraph.Skeleton lines={1} width={160} />
					</Flex>
				</Flex>
				<Box mt={3}>
					<Skeleton.Repeat count={3}>
						<Flex justifyContent="space-between" my={1} flexWrap="wrap">
							<Paragraph.Skeleton lines={1} width={90} />
							<Paragraph.Skeleton lines={1} width={70} />
						</Flex>
					</Skeleton.Repeat>
				</Box>
			</Box>
		</Card>
	);
};
